import { SparePartModel } from 'src/app/models/spare-part-model/spare-part-model';
import { SPARE_PART_DAMAGED_NO } from '../constants/spare-part-damaged-states';

/** camelCase keys allowed on SparePart payloads (aligned with backend SparePartModel DTO). */
export const SPARE_PART_CANONICAL_KEYS = [
  'sparePartId',
  'orderId',
  'sparePartName',
  'sparePartNameEs',
  'sparePartNamePt',
  'unitPrice',
  'amount',
  'taxNote',
  'ncm',
  'productCode',
  'providerName',
  'expectedArrivalDate',
  'productStatus',
  'originalProductStatus',
  'comments',
  'emergentComment',
  'effectiveEmergentComment',
  'isDamaged',
  'codBr',
  'valorDeVenta',
  'netWheight',
  'grossWheight',
  'country',
  'needReview',
  'isFant',
  'fantUrgency',
  'recompra',
  'control',
  'anulado',
  'trello',
  'devZureo',
  'shippingStatus',
  'shippingReceipt',
  'packageNumber',
  'exportNumber',
  'interno',
  'externo',
  'fechaDeComunicado',
  'color',
  'transportType',
  'receptionStatus',
  'arrivalDate',
  'packager',
  'returnStatus',
  'returnLogStatus',
  'returnReason',
  'returnDetails',
  'returnCarrier',
  'nfd',
  'mlStatus',
  'mlDeadline',
  'freteDev',
  'newOrderNumber',
  'entradaSage',
  'controleSeba',
  'okAnulacao',
  'facturado',
  'lastExportedAt',
  'fechaExportacion',
  'orderNumber',
  'orderDate',
  'orderExpectedArrivalDate',
  'siniestro',
  'vendedor',
  'fornecedor',
  'clienteUY',
  'clientName',
  'clientPhone',
  'invoiceNumber',
  'providerPhone',
  'orderStatus',
  'carMakeModel',
  'deliveryAddress',
  'isExport',
  'sendYesOrNo',
  'orderSparePartsCount',
  'orderTotalPrice'
] as const;

const PASCAL_ALIASES: Partial<Record<(typeof SPARE_PART_CANONICAL_KEYS)[number], string[]>> = {
  codBr: ['CodBR'],
  ncm: ['NCM']
};

function toPascalCase(camelKey: string): string {
  return camelKey.charAt(0).toUpperCase() + camelKey.slice(1);
}

function readCanonicalField(raw: Record<string, unknown>, camelKey: string): unknown {
  if (Object.prototype.hasOwnProperty.call(raw, camelKey)) {
    return raw[camelKey];
  }

  const pascalKey = toPascalCase(camelKey);
  if (Object.prototype.hasOwnProperty.call(raw, pascalKey)) {
    return raw[pascalKey];
  }

  const aliases = PASCAL_ALIASES[camelKey as keyof typeof PASCAL_ALIASES];
  if (aliases) {
    for (const alias of aliases) {
      if (Object.prototype.hasOwnProperty.call(raw, alias)) {
        return raw[alias];
      }
    }
  }

  return undefined;
}

/**
 * Removes PascalCase keys when a camelCase counterpart exists on the same object.
 */
export function stripPascalCaseShadowKeys<T extends Record<string, any>>(payload: T): T {
  const sanitized = { ...payload } as Record<string, any>;
  Object.keys(sanitized).forEach((key) => {
    if (/^[A-Z]/.test(key)) {
      const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
      if (Object.prototype.hasOwnProperty.call(sanitized, camelKey)) {
        delete sanitized[key];
      }
    }
  });
  return sanitized as T;
}

/**
 * Maps API/UI objects to a camelCase-only spare part shape (no shadow keys).
 */
export function toCanonicalSparePartKeys(raw: Record<string, unknown>): SparePartModel {
  const canonical: Record<string, unknown> = {};

  for (const key of SPARE_PART_CANONICAL_KEYS) {
    const value = readCanonicalField(raw, key);
    if (value !== undefined) {
      canonical[key] = value;
    }
  }

  return canonical as SparePartModel;
}

/** Default values for form fields rendered below "Comentario emergente" when duplicating a spare part. */
export function getPostEmergentCommentDuplicateDefaults(): Partial<SparePartModel> {
  return {
    returnStatus: '',
    returnLogStatus: '',
    returnReason: '',
    returnDetails: '',
    ncm: '',
    codBr: '',
    isDamaged: SPARE_PART_DAMAGED_NO,
    shippingReceipt: '',
    needReview: false
  };
}

export type FantUrgency = 'green' | 'yellow' | 'red';

export function normalizeFantUrgency(value: unknown): FantUrgency | null {
  if (value == null) return null;
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'green' || normalized === 'verde') return 'green';
  if (normalized === 'yellow' || normalized === 'amarillo') return 'yellow';
  if (normalized === 'red' || normalized === 'rojo') return 'red';
  return null;
}

/** Green is the urgency assigned when a spare part is marked as FANT and has no color yet. */
export function defaultFantUrgency(isFant: boolean, value: unknown): FantUrgency | null {
  if (!isFant) return null;
  return normalizeFantUrgency(value) ?? 'green';
}

export function applyPostEmergentCommentDuplicateDefaults<T extends SparePartModel>(sparePart: T): T {
  return {
    ...sparePart,
    ...getPostEmergentCommentDuplicateDefaults()
  };
}
